import { Dialog, DialogContent, Button, Box, Typography, Link, DialogTitle, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import TermsOfUseContent from './TermsOfUseContent';

/**
 * LgpdConsentModal - Modal de consentimento LGPD
 * 
 * Características:
 * - Modal não fechável sem consentimento
 * - Persiste consentimento em localStorage
 * - Link para página de termos de uso
 * - Conform com Lei Geral de Proteção de Dados Pessoais (Lei 13.709/2018)
 * 
 * @example
 * <LgpdConsentModal />
 */
export default function LgpdConsentModal() {
  const [open, setOpen] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const STORAGE_KEY = 'agendario_lgpd_consent';

  // Verificar ao montar o componente se já houve consentimento
  useEffect(() => {
    const hasConsent = localStorage.getItem(STORAGE_KEY);
    if (!hasConsent) {
      setOpen(true);
    }
  }, []);

  const handleAgree = () => {
    // Persistir consentimento no localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      consented: true,
      timestamp: new Date().toISOString(),
    }));
    setOpen(false);
  };

  const handleLearnMore = () => {
    // Abrir modal com os termos
    setOpenTerms(true);
  };

  return (
    <>
      {/* Modal Principal de Consentimento LGPD */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          // Impedir fechamento por escape ou clique fora
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            position: 'fixed',
            bottom: 16,
            right: 16,
            maxWidth: 320,
          },
        }}
      >
        <DialogContent
          sx={{
            padding: '10px 12px',
          }}
        >
          {/* Conteúdo principal sem título */}
          <Typography
            variant="caption"
            sx={{
              marginBottom: 1,
              lineHeight: 1.3,
              color: '#555',
              display: 'block',
              fontSize: '0.75rem',
            }}
          >
            Utilizamos cookies e dados pessoais para: manter sua conta (armazenar credenciais e preferências), segurança (auditar atividades), melhorias no serviço (entender como você utiliza nossa plataforma) e conformidade legal. Seus dados são protegidos conforme a Lei Geral de Proteção de Dados Pessoais (LGPD - Lei 13.709/2018).{' '}
            <Link
              component="button"
              variant="caption"
              onClick={(e) => {
                e.preventDefault();
                handleLearnMore();
              }}
              sx={{
                cursor: 'pointer',
                color: '#555',
                textDecoration: 'underline',
                fontSize: '0.75rem',
                '&:hover': {
                  color: '#333',
                },
              }}
            >
              Leia mais sobre proteção de dados
            </Link>
            .
          </Typography>

          {/* Botão Concordar */}
          <Button
            variant="contained"
            onClick={handleAgree}
            fullWidth
            sx={{
              minHeight: 32,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              backgroundColor: '#1976d2',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Concordar
          </Button>
        </DialogContent>
      </Dialog>

      {/* Modal Grande com Termos Completos */}
      <Dialog
        open={openTerms}
        onClose={() => setOpenTerms(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            paddingBottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Termos de Uso e Política de Privacidade
          </Typography>
          <IconButton
            onClick={() => setOpenTerms(false)}
            size="small"
            sx={{
              color: 'inherit',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: 'auto',
            paddingTop: 2,
          }}
        >
          <TermsOfUseContent />
        </DialogContent>
      </Dialog>
    </>
  );
}

LgpdConsentModal.propTypes = {};
